import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App.tsx';
import './honeycomb-assets.js';
import './index.scss';
import { apiConfig } from './config/api.config.ts';
import { themes } from './theme.ts';
import { ThemeProvider } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers-pro/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { store } from './redux/store';
import { setIsPrivateLabel } from './redux/slices/uiSettingsSlice';
import {
	getAuthTokenFromUrl,
	validateAuthToken,
	removeAuthTokenFromUrl,
} from './utils/validateAuthToken';
import { AuthTokenValidation } from './components/AuthTokenValidation';
import TagManager from 'react-gtm-module';

async function enableMocking() {
	if (!apiConfig.useMocks) {
		return;
	}
	const { worker } = await import('./mocks/browser');
	return worker.start();
}

const detectTheme = () => {
	// TODO: Update URL detection based on Prod pattern
	const url = new URL(window.location.href);
	const isPrivateLabel = url.searchParams.get('privateLabel') === 'true';

	store.dispatch(setIsPrivateLabel(isPrivateLabel));

	return isPrivateLabel ? themes.privateLabel : themes.main;
};

/**
 * Handles cross-domain authentication token validation
 * If an authToken is present in the URL, validate it before rendering the app
 */
async function handleAuthToken(): Promise<boolean> {
	const token = getAuthTokenFromUrl();

	if (!token) {
		return true; // No token to validate, proceed normally
	} else {
		// immediately remove the authToken from the URL for security
		removeAuthTokenFromUrl();
	}

	// Show loading UI while validating
	const loadingRoot = createRoot(document.getElementById('root')!);
	loadingRoot.render(
		<StrictMode>
			<ThemeProvider theme={detectTheme()}>
				<AuthTokenValidation />
			</ThemeProvider>
		</StrictMode>
	);

	// Validate the token
	const result = await validateAuthToken(token);

	// Unmount loading UI
	loadingRoot.unmount();

	if (result.success) {
		//  proceed to app
		return true;
	} else {
		// Validation failed, redirect to login
		window.location.href = '/login';
		return false;
	}
}

enableMocking().then(async () => {
	// Handle auth token validation before rendering the app
	const shouldRenderApp = await handleAuthToken();

	if (shouldRenderApp) {
		TagManager.initialize({ gtmId: import.meta.env.VITE_GTM_ID });

		createRoot(document.getElementById('root')!).render(
			<StrictMode>
				<ThemeProvider theme={detectTheme()}>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<App />
					</LocalizationProvider>
				</ThemeProvider>
			</StrictMode>
		);
	}
});
